import { ColumnDef } from '@tanstack/react-table';
import {
  EOrderStatus,
  SortingDirection,
  SortingOrderValue
} from 'constants/enums';
import { formatDateTimeByLocale } from 'utils/formattingUtil';

import { CreditCardPaymentMethod } from 'components/Order/Components/CreditCardPaymentMethod';

import { OrderCellStatus } from './Components/OrderCellStatus';
import { TableCellEllipsis } from './Components/TableCellEllipsis';
import { sortByDate } from './sortingUtils';
import { OrderTableRow } from './utils';

export const ORDER_TABLE_COLUMNS: ColumnDef<OrderTableRow>[] = [
  {
    accessorKey: 'project',
    header: 'Project Name',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'paymentMethod',
    header: 'Payment Method',
    cell: ({ row }) => (
      <TableCellEllipsis>
        <CreditCardPaymentMethod paymentMethod={row.original.paymentMethod} />
      </TableCellEllipsis>
    )
  },
  {
    accessorKey: 'price',
    header: 'Price',
    cell: ({ getValue, row }) => (
      <TableCellEllipsis>
        {row.original.currencySymbol}
        {getValue()?.toString()}
      </TableCellEllipsis>
    )
  },
  {
    accessorKey: 'date',
    header: 'Date',
    cell: ({ getValue }) => (
      <TableCellEllipsis>
        {formatDateTimeByLocale(getValue() as string)}
      </TableCellEllipsis>
    ),
    sortingFn: sortByDate,
    sortDescFirst: true,
    enableSorting: true
  },
  {
    accessorKey: 'orderId',
    header: 'Order ID',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'playerId',
    header: 'Player ID',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'playerEmail',
    header: 'Player Email',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'name',
    header: 'Offer Name',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'deviceOS',
    header: 'Device OS',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'publisherPurchaseId',
    header: 'Transaction ID',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <OrderCellStatus {...row.original} />
  }
];

export const FREE_ORDER_TABLE_COLUMNS: ColumnDef<OrderTableRow>[] = [
  {
    accessorKey: 'project',
    header: 'Project',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'date',
    header: 'Date',
    cell: ({ getValue }) => (
      <TableCellEllipsis>
        {formatDateTimeByLocale(getValue() as string)}
      </TableCellEllipsis>
    ),
    sortingFn: sortByDate,
    sortDescFirst: true,
    enableSorting: true
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'price',
    header: 'Price',
    cell: () => <TableCellEllipsis>Free</TableCellEllipsis>,
    enableSorting: false
  },
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'orderId',
    header: 'Order ID',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'playerId',
    header: 'Player ID',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'deviceOS',
    header: 'Device OS',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'publisherPurchaseId',
    header: 'Transaction ID',
    cell: ({ getValue }) => (
      <TableCellEllipsis>{getValue() as string}</TableCellEllipsis>
    )
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => <OrderCellStatus {...row.original} />
  }
];

export enum OrderFilterKey {
  PLAYER_EMAIL = 'playerEmail',
  STATUSES = 'statuses',
  PUBLISHER_PURCHASE_ID = 'publisherPurchaseId',
  RECEIPT_ID = 'receiptId',
  ORDER_ID = 'orderId',
  PLAYER_ID = 'playerId',
  OS = 'os',
  PROJECTS = 'projects',
  TRANSACTION_ID = 'transactionId',
  PAYMENT_FLOW = 'paymentFlow',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  IS_FREE = 'isFree'
}

export const VALID_ORDER_STATUSES = [
  EOrderStatus.created,
  EOrderStatus.payment_pending,
  EOrderStatus.payment_succeed,
  EOrderStatus.payment_failed,
  EOrderStatus.payment_refund_pending,
  EOrderStatus.payment_refund_requires_action,
  EOrderStatus.payment_refunded,
  EOrderStatus.payment_refund_failed,
  EOrderStatus.payment_refund_canceled,
  EOrderStatus.payment_canceled,
  EOrderStatus.charge_pending,
  EOrderStatus.charge_succeed,
  EOrderStatus.charge_failed,
  EOrderStatus.payment_dispute_open,
  EOrderStatus.payment_dispute_lost,
  EOrderStatus.payment_dispute_won,
  EOrderStatus.payment_dispute_evidence_submitted,
  EOrderStatus.payment_dispute_inquiry_open,
  EOrderStatus.payment_dispute_inquiry_evidence_submitted,
  EOrderStatus.payment_dispute_inquiry_won
];

export const ORDER_TABLE_ROWS_PER_PAGE = 200;

export const ORDER_TYPE_OPTIONS = [
  { label: 'Free Orders', value: 'true' },
  { label: 'Paid Orders', value: 'false' }
];

export const DEFAULT_SORTING = {
  sortValue: SortingOrderValue.DATE,
  direction: SortingDirection.DESC
};
